import { z } from "zod";

export const ReviewFactInput = z.object({
  claim: z.string().min(1),
  status: z.enum(["verified", "unverified", "disputed"]),
  source: z
    .string()
    .optional()
    .describe(
      "Short human-readable citation shown under the claim, e.g. " +
        "'Live store page, fetched 2026-07-16'. This is a label, not a link.",
    ),
  sourceUrl: z
    .string()
    .url()
    .optional()
    .describe(
      "Full https:// URL backing the claim, so the reviewer can open the " +
        "source. Provide it whenever the fact points at a specific page; " +
        "omit it if there is no URL (never put a non-URL label here).",
    ),
  anchor: z.string().optional(),
});
export type ReviewFactInput = z.infer<typeof ReviewFactInput>;

export const ReviewLinkInput = z.object({
  url: z.string().url(),
  note: z.string().optional(),
  relevance: z.enum(["supports", "weak", "broken"]).optional(),
  anchor: z.string().optional(),
});
export type ReviewLinkInput = z.infer<typeof ReviewLinkInput>;

export const ReviewChangeInput = z.object({
  kind: z.enum(["edit", "add", "remove", "replace"]),
  title: z.string().min(1),
  before: z.string().optional(),
  after: z.string().optional(),
});
export type ReviewChangeInput = z.infer<typeof ReviewChangeInput>;

export const CreateContentReviewRequest = z.object({
  title: z.string().min(1),
  original: z.string(),
  revised: z.string(),
  facts: z.array(ReviewFactInput).optional(),
  links: z.array(ReviewLinkInput).optional(),
  changes: z.array(ReviewChangeInput).optional(),
});
export type CreateContentReviewRequest = z.infer<
  typeof CreateContentReviewRequest
>;

export const CreateContentReviewResponse = z.object({
  id: z.string().uuid(),
  url: z.string().url(),
});
export type CreateContentReviewResponse = z.infer<
  typeof CreateContentReviewResponse
>;

// --- Read-back loop -----------------------------------------------------
// The reviewer's decision, submitted from the (unauthenticated) review
// page and read back by the MCP consumer (Claude) via
// ripenn_get_content_review_result.

export const ReviewDecision = z.enum([
  "approved",
  "changes_requested",
  "assigned_back",
]);
export type ReviewDecision = z.infer<typeof ReviewDecision>;

/** One rail item (fact / link / change) and the reviewer's mark on it. */
export const ReviewedItem = z.object({
  id: z.string(), // f1 / l1 / c1
  kind: z.enum(["fact", "link", "change"]),
  label: z.string(), // claim / url / change title — self-describing for the consumer
  mark: z.enum(["accepted", "flagged"]).nullable(),
});
export type ReviewedItem = z.infer<typeof ReviewedItem>;

/** A concrete edit the reviewer suggested (span edit or paragraph rewrite). */
export const SuggestedEdit = z.object({
  target: z.string(), // fact/link id or paragraph id
  original: z.string(),
  suggested: z.string(),
});
export type SuggestedEdit = z.infer<typeof SuggestedEdit>;

export const ContentReviewResult = z.object({
  decision: ReviewDecision,
  note: z.string(), // reviewer summary; may be empty
  items: z.array(ReviewedItem),
  suggestedEdits: z.array(SuggestedEdit),
  submittedAt: z.string().datetime(),
});
export type ContentReviewResult = z.infer<typeof ContentReviewResult>;

/** Reviewer's browser POSTs this; the server stamps `submittedAt`. */
export const SubmitContentReviewResultRequest = ContentReviewResult.omit({
  submittedAt: true,
});
export type SubmitContentReviewResultRequest = z.infer<
  typeof SubmitContentReviewResultRequest
>;

export const SubmitContentReviewResultResponse = z.object({
  ok: z.literal(true),
});
export type SubmitContentReviewResultResponse = z.infer<
  typeof SubmitContentReviewResultResponse
>;

export const GetContentReviewResultResponse = z.object({
  status: z.enum(["pending", "resolved"]),
  result: ContentReviewResult.nullable(),
});
export type GetContentReviewResultResponse = z.infer<
  typeof GetContentReviewResultResponse
>;
